import React, { useCallback, useEffect, useState } from 'react';
import { Button, ListGroup, ListGroupItem } from 'reactstrap';
import InstructionAssistantModalShell from './InstructionAssistantModalShell';

// Comprehend brand green, applied inline (not a Bootstrap theme color).
const GREEN = '#1d9e75';

const primaryButtonStyle = {
  borderRadius: 12,
  fontSize: 15,
  fontWeight: 700,
  minHeight: 44,
};

const choiceButtonStyle = {
  borderRadius: 12,
  padding: '10px 12px',
};

const renderExamples = (examples) => {
  if (!Array.isArray(examples) || examples.length === 0) {
    return <div className="small text-muted">(no examples)</div>;
  }
  return (
    <ol className="small pl-3 mb-0">
      {examples.map((example, index) => (
        <li key={index} className="text-break mb-1" style={{ whiteSpace: 'pre-wrap' }}>
          {String(example)}
        </li>
      ))}
    </ol>
  );
};

export const getInstructionDisplayParts = (instruction) => {
  const fallback =
    typeof instruction === 'string' && instruction
      ? instruction
      : '(no instruction)';
  try {
    const parsed =
      typeof instruction === 'string' ? JSON.parse(instruction) : instruction;
    if (!parsed || typeof parsed.TopLevelInstruction !== 'string') {
      return { topLevel: fallback, subsections: [] };
    }
    let subsections = parsed.SubSections || {};
    if (typeof subsections === 'string') {
      try {
        subsections = JSON.parse(subsections);
      } catch {
        subsections = {};
      }
    }
    return {
      topLevel: parsed.TopLevelInstruction || '(no top-level instruction)',
      subsections: Object.entries(subsections)
        .filter(([, value]) => value && value.Instruction)
        .map(([name, value]) => ({ name, instruction: value.Instruction })),
    };
  } catch {
    return { topLevel: fallback, subsections: [] };
  }
};

const renderInstruction = (instruction) => {
  const display = getInstructionDisplayParts(instruction);
  return (
    <div className="small text-break" style={{ whiteSpace: 'pre-wrap' }}>
      <div>{display.topLevel}</div>
      {display.subsections.length > 0 && (
        <div className="mt-2">
          <div className="font-weight-bold">View More instructions</div>
          {display.subsections.map((subsection) => (
            <div key={subsection.name} className="mt-1">
              <span className="font-weight-bold">{subsection.name}: </span>
              {subsection.instruction}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InstructionUpdateUndoModal = ({
  isOpen,
  kind,
  oldInstructions = {},
  newInstructions = {},
  oldExamples = {},
  newExamples = {},
  onResolveInstructions,
}) => {
  const sections = [
    ...new Set([
      ...Object.keys(newInstructions || {}),
      ...Object.keys(newExamples || {}),
    ]),
  ];
  const isTemplateOnly = kind === 'template';
  // Sections the user has chosen to change back to the original.
  const [revertSections, setRevertSections] = useState([]);
  // Sections whose original/updated preferences are expanded for comparison.
  const [expandedSections, setExpandedSections] = useState({});

  // Reset the per-section choices each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setRevertSections([]);
      setExpandedSections({});
    }
  }, [isOpen, newInstructions, newExamples]);

  const closeModal = useCallback(
    () => onResolveInstructions([]),
    [onResolveInstructions],
  );

  const setRevert = (section, revert) =>
    setRevertSections((prev) =>
      revert
        ? prev.includes(section)
          ? prev
          : [...prev, section]
        : prev.filter((s) => s !== section),
    );

  const toggleExpanded = (section) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  return (
    <InstructionAssistantModalShell
      isOpen={isOpen}
      title="Undo changes?"
      ariaLabel="Instruction and example changes"
      iconClassName="fa fa-rotate-left"
      onClose={closeModal}
      footer={
        <div className="w-100">
          <Button
            type="button"
            color="success"
            block
            className="font-weight-bold shadow-sm"
            style={{
              ...primaryButtonStyle,
              backgroundColor: GREEN,
              borderColor: GREEN,
            }}
            onClick={() => onResolveInstructions(revertSections)}
          >
            Confirm choices
          </Button>
          {sections.length > 1 && (
            <div className="text-center mt-2">
              <Button
                type="button"
                color="link"
                size="sm"
                className="font-weight-bold"
                style={{ color: GREEN }}
                onClick={() => setRevertSections([])}
              >
                Keep current for all
              </Button>
              <Button
                type="button"
                color="link"
                size="sm"
                className="text-muted"
                onClick={() => setRevertSections(sections)}
              >
                {isTemplateOnly
                  ? 'Restore original for all'
                  : 'Go back for all'}
              </Button>
            </div>
          )}
        </div>
      }
    >
      <p className="text-muted mb-2" style={{ fontSize: 14, lineHeight: 1.5 }}>
        {isTemplateOnly
          ? 'Pick Current to keep the updated preference, or Previous to restore the original preference.'
          : "Pick Current to keep the updated note and saved preference, or Previous to restore that section's earlier note text and preference."}
      </p>
      <ListGroup tag="ul" flush>
        {sections.map((section) => {
          const restoringOriginal = revertSections.includes(section);
          const isExpanded = !!expandedSections[section];
          const instructionChanged = Object.prototype.hasOwnProperty.call(
            newInstructions,
            section,
          );
          const examplesChanged = Object.prototype.hasOwnProperty.call(
            newExamples,
            section,
          );
          const originalInstruction = oldInstructions[section] || '(no instruction)';
          const updatedInstruction = newInstructions[section] || '(no instruction)';

          return (
            <ListGroupItem tag="li" key={section} className="px-0 py-2">
              <div className="d-flex align-items-start justify-content-between mb-2">
                <span
                  className="small text-uppercase font-weight-bold text-truncate mr-2"
                  style={{ color: GREEN, fontSize: 12, minWidth: 0 }}
                  title={section}
                >
                  {section}
                </span>
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="p-0 flex-shrink-0"
                  style={{ boxShadow: 'none', color: GREEN, fontSize: 13 }}
                  onClick={() => toggleExpanded(section)}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? 'Hide' : 'Details'}
                  <i
                    className={
                      'fa fa-chevron-' + (isExpanded ? 'up ml-1' : 'down ml-1')
                    }
                    aria-hidden="true"
                  />
                </Button>
              </div>
              <div
                className="row no-gutters"
                role="radiogroup"
                aria-label={`Undo choice for ${section}`}
              >
                <div className="col pr-1">
                  <Button
                    type="button"
                    role="radio"
                    aria-checked={!restoringOriginal}
                    color={!restoringOriginal ? 'success' : 'secondary'}
                    outline={restoringOriginal}
                    className="text-left h-100"
                    style={{
                      ...choiceButtonStyle,
                      ...(!restoringOriginal
                        ? {
                            backgroundColor: GREEN,
                            borderColor: GREEN,
                            color: '#fff',
                          }
                        : {}),
                    }}
                    block
                    onClick={() => setRevert(section, false)}
                  >
                    <span className="d-block font-weight-bold">
                      Current (updated)
                    </span>
                    <span className="d-block small">
                      {isTemplateOnly
                        ? 'Keep saved changes'
                        : 'Keep note and preference'}
                    </span>
                  </Button>
                </div>
                <div className="col pl-1">
                  <Button
                    type="button"
                    role="radio"
                    aria-checked={restoringOriginal}
                    color={restoringOriginal ? 'success' : 'secondary'}
                    outline={!restoringOriginal}
                    className="text-left h-100"
                    style={{
                      ...choiceButtonStyle,
                      ...(restoringOriginal
                        ? {
                            backgroundColor: GREEN,
                            borderColor: GREEN,
                            color: '#fff',
                          }
                        : {}),
                    }}
                    block
                    onClick={() => setRevert(section, true)}
                  >
                    <span className="d-block font-weight-bold">
                      Previous (original)
                    </span>
                    <span className="d-block small">
                      {isTemplateOnly
                        ? 'Restore saved changes'
                        : 'Restore note and preference'}
                    </span>
                  </Button>
                </div>
              </div>
              {isExpanded && (
                <div className="mt-3 pt-3 border-top">
                  <div
                    className="pl-3 mb-3"
                    style={{ borderLeft: `3px solid ${GREEN}` }}
                  >
                    <div
                      className="small text-uppercase font-weight-bold mb-1"
                      style={{ color: GREEN }}
                    >
                      Original
                    </div>
                    {instructionChanged && (
                      renderInstruction(originalInstruction)
                    )}
                    {examplesChanged && (
                      <div className={instructionChanged ? 'mt-2' : ''}>
                        <div
                          className="small text-uppercase font-weight-bold mb-1"
                          style={{ color: GREEN }}
                        >
                          Examples
                        </div>
                        {renderExamples(oldExamples[section])}
                      </div>
                    )}
                  </div>
                  <div
                    className="pl-3"
                    style={{ borderLeft: `3px solid ${GREEN}` }}
                  >
                    <div
                      className="small text-uppercase font-weight-bold mb-1"
                      style={{ color: GREEN }}
                    >
                      Updated
                    </div>
                    {instructionChanged && (
                      renderInstruction(updatedInstruction)
                    )}
                    {examplesChanged && (
                      <div className={instructionChanged ? 'mt-2' : ''}>
                        <div
                          className="small text-uppercase font-weight-bold mb-1"
                          style={{ color: GREEN }}
                        >
                          Examples
                        </div>
                        {renderExamples(newExamples[section])}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </ListGroupItem>
          );
        })}
      </ListGroup>
    </InstructionAssistantModalShell>
  );
};

export default InstructionUpdateUndoModal;
